import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { afterSignIn } from "./AuthCallback";

// Sign in by typing an email and the 6-digit code sent to it, so you choose exactly which account
export const EmailSignIn = ({ className = "" }) => {
  const qc = useQueryClient();
  const { setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  useEffect(() => {
    if (!wait) return undefined;
    const t = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  const reset = (o) => { setOpen(o); if (!o) { setSent(false); setCode(""); } };

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.otpRequest(email.trim());
      if (sent) toast.success("New code sent");
      setSent(true); setCode(""); setWait(45);
    }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send the code"); }
    setBusy(false);
  };
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await api.otpVerify(email.trim(), code);
      setOpen(false);
      await afterSignIn(u, { qc, setUser });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That code didn't work");
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`flex items-center gap-2 text-sm text-slate-300 hover:text-emerald-300 transition-colors ${className}`} data-testid="email-login-button">
        <Mail size={16} /> Sign in with an email code
      </button>
      <Dialog open={open} onOpenChange={reset}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-sm w-[calc(100%-2rem)] rounded-2xl" data-testid="email-login-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{sent ? "Check your email" : "Sign in with an email code"}</DialogTitle>
            <DialogDescription>{sent ? <>We sent a 6-digit code to <b className="text-slate-200">{email.trim()}</b>. It works for 10 minutes.</> : "Type the email of the account you want. We'll send you a code — no password needed."}</DialogDescription>
          </DialogHeader>
          {!sent ? (
            <form onSubmit={send} className="space-y-3">
              <Input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoCapitalize="none" autoCorrect="off" enterKeyHint="send" data-testid="email-login-input" />
              <Button type="submit" disabled={busy || !email.includes("@")} className="w-full bg-emerald-400 text-[#04120c] hover:bg-emerald-300" data-testid="email-login-send">{busy ? "Sending…" : "Send code"}</Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-3">
              <Input inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456"
                className="text-center text-xl tracking-[0.4em] font-mono" enterKeyHint="go" data-testid="email-login-code" />
              <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-emerald-400 text-[#04120c] hover:bg-emerald-300" data-testid="email-login-verify">{busy ? "Signing in…" : "Sign in"}</Button>
              <div className="flex justify-between text-xs text-slate-400">
                <button type="button" onClick={() => setSent(false)} className="hover:text-slate-200" data-testid="email-login-change">Use a different email</button>
                <button type="button" onClick={send} disabled={busy || wait > 0} className="hover:text-slate-200 disabled:opacity-60" data-testid="email-login-resend">{wait > 0 ? `New code in ${wait}s` : "Send a new code"}</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
